import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useModals } from "../../context/ModalContext";
import { gateway } from "../../gateway";
import { updateUser } from "../../redux/features/userStateSlice";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { WidgetComponentMaps } from "../common/WidgetComponentMaps";
import { HoursOfOperation } from "../hoursOfOperation";
import { CompanyTimezoneModalContainer } from "../shared/components/features/event-scheduler/components/timezone/TimeZoneConfiguration";
import { useCompanyInfo } from "../shared/hooks/useCompany";
import { colors } from "../ui/colors";
import { filledButton, outLinedButton } from "../ui/styles/buttonStyles";

export const HoursOfOperationWidget = ({ swapyName = "hoursOfOperationWidget", sx }) => {
  const dispatch = useDispatch();
  const tokenLocal = sessionStorage.getItem("token");
  const [availability, setMyAvailability] = useState();

  const companyData = useCompanyInfo();
  const { closeByKey } = useModals();

  const companyTimeZone = companyData.data?.timeZone ?? null;

  const getMyAvailability = async () => {
    try {
      const response = await gateway.get(`/availability/self`, {
        headers: { Authorization: `${tokenLocal}` },
      });
      const data = response.data.data.availability;
      dispatch(updateUser({ availability: data }));
      setMyAvailability(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleTimeZoneSaved = async () => {
    await companyData.refetch();
    closeByKey("Organization-TimeZone-Business");
  };

  useEffect(() => {
    getMyAvailability();
  }, []);

  const displayHoursSummary = () => {
    if (!availability) return null;

    const days = Object.entries(availability);
    const openDays = days.filter(([_, day]) => day.isOpen);
    const closedDays = days.filter(([_, day]) => !day.isOpen);

    const renderDayBox = (title, items, isClosed) => (
      <Box
        sx={{
          borderRadius: 2,
          border: `1px solid ${colors.gray[100]}`,
          p: 2,
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.gray[700],
            mb: 1,
            fontWeight: 600,
          }}
        >
          {title} ({items.length})
        </Typography>

        {items.map(([day, schedule], index) => (
          <Box
            key={day}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 0.75,
              borderBottom: index < items.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: isClosed ? colors.gray[400] : colors.gray[800],
                fontWeight: isClosed ? 400 : 500,
                textTransform: "capitalize",
              }}
            >
              {day}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: isClosed ? colors.gray[400] : colors.gray[600],
              }}
            >
              {schedule.from} - {schedule.to}
            </Typography>
          </Box>
        ))}
      </Box>
    );

    return (
      <>
        <Box
          sx={{
            borderRadius: 2,
            border: `1px solid ${colors.gray[100]}`,
            p: 2,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            mb: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.gray[700],
              mb: 0.5,
              fontWeight: 600,
            }}
          >
            Time Zone
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: companyTimeZone ? colors.gray[700] : colors.gray[400],
            }}
          >
            {companyTimeZone || "Not configured"}
          </Typography>
        </Box>

        {renderDayBox("Open Days", openDays, false)}
        {renderDayBox("Closed Days", closedDays, true)}
      </>
    );
  };

  return (
    <WidgetComponentMaps
      swapyName={swapyName}
      title="Hours of Operation"
      description="Review and manage the business hours for each day of the week."
      icon={<AccessTimeIcon />}
      sxProp={{
        ...sx,
        height: { xs: "65vh", md: "70vh", lg: "100%" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>{displayHoursSummary()}</Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "center",
          bgcolor: "transparent",
          flexWrap: "wrap",
        }}
      >
        <BaseModalComponent
          modalKey="Organization-TimeZone-Business"
          modalLabel="Configure Time Zone"
          defaultSizes={{ width: 600, height: 550 }}
          elementTrigger={<Button sx={filledButton}>Time Zone</Button>}
          onCloseTheme="color"
        >
          <CompanyTimezoneModalContainer initialTimeZone={companyTimeZone} onSaved={handleTimeZoneSaved} />
        </BaseModalComponent>

        <BaseModalComponent
          modalKey="Organization-ManageHours-Business"
          modalLabel="Manage Hours"
          sx={{
            width: { xs: "90%", sm: "600px" },
            height: { xs: "95%", sm: "90%", md: "100%", lg: "100%" },
          }}
          elementTrigger={<Button sx={filledButton}>Manage Hours</Button>}
          onCloseTheme="color"
        >
          <HoursOfOperation
            updateComponentState={updateUser}
            sx={{
              width: "100%",
              height: "100%",
            }}
          />
        </BaseModalComponent>
      </Box>
    </WidgetComponentMaps>
  );
};
